import { formatTime } from "lib/format-time"
import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"
import {
    MOVEMENTS_AMENDS_KEY,
    TITLE_AMENDS_KEY,
    UNIT_OF_MEASURE_AMENDS_KEY,
} from "authoring/routes/project/amends/lib/constants/amends-keys"
import { formatValueOrDash, normaliseText } from "./components/pdf-formatters"
import { buildSideBySideTaskRows, detectTaskOrderEntries, buildTaskOrderChangesSection } from "./pdf-task-ordering.mjs"
import { normalizeAuthoringComparisonText } from "authoring/routes/project/schedule-editor/lib/authoring-comparison-normalization"

export function buildViewData(exportData, mode = "differences") {
    const draft = exportData?.draft ?? {}
    const original = exportData?.original ?? {}
    const comparisonDraftVersion = getCandidateVersion(original?.version, draft?.version)
    const amendsByKey = exportData?.amendsByKey ?? {}
    const draftTasks = draft.tasks ?? []
    const originalTasks = original.tasks ?? []
    const draftTaskIndexLookup = buildTaskIndexLookup(draftTasks)
    const originalTaskLookup = buildTaskLookup(originalTasks)
    const draftTaskLookup = buildTaskLookup(draftTasks)
    const draftDisplayTasks = withDisplayIndexes(draftTasks, draftTaskIndexLookup)
    const originalTaskIndexLookup = buildTaskIndexLookup(originalTasks)
    const originalDisplayTasks = withDisplayIndexes(originalTasks, originalTaskIndexLookup)
    const taskChanges = getTaskChanges({ beforeTasks: originalDisplayTasks, afterTasks: draftDisplayTasks })
    const changedTaskIds = new Set(taskChanges.map((item) => item.taskId))
    const orderEntries = detectTaskOrderEntries(originalDisplayTasks, draftDisplayTasks, { normaliseText })
    const introChanged =
        normalizeAuthoringComparisonText(draft?.intro?.content) !==
        normalizeAuthoringComparisonText(original?.intro?.content)
    const noteChanged =
        normalizeAuthoringComparisonText(draft?.intro?.note) !== normalizeAuthoringComparisonText(original?.intro?.note)
    const unitOfMeasureChanged = normaliseText(draft?.unitOfMeasure) !== normaliseText(original?.unitOfMeasure)
    const sideBySideTaskRows = filterTaskRowsForMode(
        buildSideBySideTaskRows({
            originalTasks: originalDisplayTasks,
            draftTasks: draftDisplayTasks,
            originalTaskLookup,
            draftTaskLookup,
            changedTaskIds,
            amendsByKey,
            normaliseText,
            buildTaskChangeSummary,
        }),
        mode
    )
    const referencesSection = buildReferencesSection({
        mode,
        originalLegislation: original.legislation,
        draftLegislation: draft.legislation,
    })
    const taskOrderChangesSection = buildTaskOrderChangesSection({
        orderEntries,
        note: amendsByKey[MOVEMENTS_AMENDS_KEY] ?? null,
        normaliseText,
    })
    const comparableRows = sideBySideTaskRows.filter((item) => !item.isHeader)

    markLastComparisonRow(sideBySideTaskRows)

    return {
        mode,
        draft,
        original,
        versionInfo: exportData?.versionInfo ?? null,
        amendsByKey,
        introChanged,
        noteChanged,
        showIntroSection: mode === "side-by-side" || introChanged,
        showNotesSection: mode === "side-by-side" || noteChanged,
        showUnitOfMeasureSection: mode === "side-by-side" || unitOfMeasureChanged,
        unitOfMeasureChanged,
        unitOfMeasureNote: amendsByKey[UNIT_OF_MEASURE_AMENDS_KEY] ?? null,
        leadingSummary: stripLeadingSummaryPrefix(amendsByKey.leadingSummary?.content || ""),
        sideBySideTaskRows,
        referencesSection,
        taskOrderChangesSection,
        titleNote: amendsByKey[TITLE_AMENDS_KEY] ?? null,
        draftTaskIndexLookup,
        comparisonDraftVersion,
        summary: {
            newTasks: comparableRows.filter((item) => item.status === "new").length,
            modifiedTasks: comparableRows.filter((item) => item.status === "modified").length,
            removedTasks: comparableRows.filter((item) => item.status === "removed").length,
            totalTasks: countComparableTasks(draftTasks),
        },
    }
}

function getCandidateVersion(originalVersion, draftVersion) {
    const numericOriginalVersion = Number(originalVersion)
    if (Number.isFinite(numericOriginalVersion)) return `${numericOriginalVersion + 1}`
    return draftVersion ?? null
}

function filterTaskRowsForMode(rows = [], mode) {
    if (mode === "side-by-side") return rows
    return rows.filter((item) => item.status !== "unchanged")
}

function buildTaskLookup(tasks = []) {
    const lookup = new Map()
    for (const task of tasks) {
        const key = task.taskId ?? task.id
        if (!key || task.isHeader) continue
        lookup.set(key, task)
    }
    return lookup
}

function buildTaskIndexLookup(tasks = []) {
    const lookup = {}
    let index = 0
    for (const task of tasks) {
        if (task?.isHeader) continue
        index += 1
        lookup[task.taskId ?? task.id] = index
    }
    return lookup
}

function withDisplayIndexes(tasks = [], taskIndexLookup = {}) {
    return tasks.map((task) => ({
        ...task,
        displayIndex: task.isHeader ? null : taskIndexLookup[task.taskId ?? task.id],
    }))
}

function countComparableTasks(tasks = []) {
    return tasks.filter((task) => !task?.isHeader).length
}

function getTaskChanges({ beforeTasks = [], afterTasks = [] } = {}) {
    const beforeLookup = buildComparableTaskLookup(beforeTasks)
    const afterLookup = buildComparableTaskLookup(afterTasks)
    const taskIds = new Set([...Object.keys(beforeLookup), ...Object.keys(afterLookup)])
    const changes = []

    for (const taskId of taskIds) {
        const beforeTask = beforeLookup[taskId]
        const afterTask = afterLookup[taskId]

        if (!beforeTask && afterTask) {
            changes.push({ taskId, changeType: AmendsChangeType.ADDED })
            continue
        }

        if (beforeTask && !afterTask) {
            changes.push({ taskId, changeType: AmendsChangeType.REMOVED })
            continue
        }

        if (!isComparableTaskEqual(beforeTask, afterTask)) {
            changes.push({ taskId, changeType: AmendsChangeType.UPDATED })
        }
    }

    return changes
}

function buildComparableTaskLookup(tasks = []) {
    return (tasks ?? []).reduce((lookup, task) => {
        const taskId = task?.isHeader ? task?.headerTaskId : task?.taskId ?? task?.id
        if (!taskId) return lookup

        lookup[taskId] = {
            title: normalizeAuthoringComparisonText(task?.title),
            content: normalizeAuthoringComparisonText(task?.content),
            note: normalizeAuthoringComparisonText(task?.note),
            classification: task?.classification ?? "",
            minutes: task?.minutes ?? null,
            skill: task?.skill?.Skilling ?? task?.skill?.SkillingCode ?? task?.skillSet ?? "",
            frequency: `${task?.frequency?.interval ?? ""}:${task?.frequency?.period ?? ""}`,
        }
        return lookup
    }, {})
}

function isComparableTaskEqual(beforeTask, afterTask) {
    return JSON.stringify(beforeTask) === JSON.stringify(afterTask)
}

function buildTaskChangeSummary(originalTask, draftTask) {
    if (!originalTask || !draftTask) return []

    const changes = []
    pushTaskChange(changes, "Criticality", originalTask.classification, draftTask.classification, {
        valueType: "classification",
    })
    pushTaskChange(changes, "Frequency", formatTaskFrequency(originalTask), formatTaskFrequency(draftTask))
    pushTaskChange(changes, "Order", formatTaskOrder(originalTask), formatTaskOrder(draftTask), {
        requireBoth: true,
    })
    pushTaskChange(changes, "Duration", formatTaskDuration(originalTask), formatTaskDuration(draftTask))

    return changes.map((item, index) => ({
        ...item,
        isLast: index === changes.length - 1,
    }))
}

function pushTaskChange(changes, label, from, to, options = {}) {
    if (options.requireBoth && (!from || !to)) return
    if (from === to) return
    changes.push({
        label,
        from: formatValueOrDash(from),
        to: formatValueOrDash(to),
        valueType: options.valueType,
    })
}

function formatTaskFrequency(task) {
    return task?.frequency?.interval && task?.frequency?.period
        ? `${task.frequency.interval}${String(task.frequency.period).charAt(0)}`
        : null
}

function formatTaskOrder(task) {
    return task?.displayIndex ? `Task ${task.displayIndex}` : null
}

function formatTaskDuration(task) {
    return task?.minutes != null ? formatTime(Math.round(task.minutes)) : null
}

function buildReferencesSection({ mode, originalLegislation = [], draftLegislation = [] }) {
    const originalLookup = buildLegislationLookup(originalLegislation)
    const draftLookup = buildLegislationLookup(draftLegislation)
    const removedItems = originalLegislation
        .filter((item) => !draftLookup.has(getLegislationKey(item)))
        .map((item) => buildLegislationItem(item, "removed"))
    const addedItems = draftLegislation
        .filter((item) => !originalLookup.has(getLegislationKey(item)))
        .map((item) => buildLegislationItem(item, "added"))

    if (mode === "differences") {
        return {
            title: "References",
            changed: removedItems.length > 0 || addedItems.length > 0,
            leftItems: removedItems,
            rightItems: addedItems,
        }
    }

    return {
        title: "References",
        changed: removedItems.length > 0 || addedItems.length > 0,
        leftItems: originalLegislation.map((item) =>
            buildLegislationItem(item, draftLookup.has(getLegislationKey(item)) ? "unchanged" : "removed")
        ),
        rightItems: draftLegislation.map((item) =>
            buildLegislationItem(item, originalLookup.has(getLegislationKey(item)) ? "unchanged" : "added")
        ),
    }
}

function buildLegislationLookup(items = []) {
    return new Set((items ?? []).map(getLegislationKey).filter(Boolean))
}

function buildLegislationItem(item, status) {
    return {
        key: getLegislationKey(item),
        title: item?.HTML ?? item?.title ?? String(item ?? ""),
        status,
    }
}

function getLegislationKey(item = {}) {
    return String(item?.ScheduleLegislationID ?? item?.HTML ?? item ?? "")
}

function markLastComparisonRow(rows = []) {
    for (const [index, item] of rows.entries()) {
        item.isLastComparisonRow = index === rows.length - 1
    }
}

function stripLeadingSummaryPrefix(html = "") {
    return String(html || "").replace(/^\s*<p>\s*This is the leading summary of changes:\s*<\/p>\s*/i, "")
}
